import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface DsOtpFieldProps {
  length?: number
  onSubmit: (code: string) => void
  onChange?: (code: string) => void
  error?: string | null
  autoFocus?: boolean
  disabled?: boolean
  label?: string
  className?: string
}

export function DsOtpField({ length = 4, onSubmit, onChange, error, autoFocus = true, disabled, label = 'One-time code', className }: DsOtpFieldProps) {
  const [digits, setDigits] = useState<string[]>(() => Array(length).fill(''))
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const [shake, setShake] = useState(0)
  useEffect(() => {
    if (error) setShake((s) => s + 1)
  }, [error])

  const update = (next: string[]) => {
    setDigits(next)
    const code = next.join('')
    onChange?.(code)
    if (code.length === length && next.every(Boolean)) onSubmit(code)
  }
  const fill = (text: string, from = 0) => {
    const chars = text.replace(/\D/g, '').slice(0, length - from).split('')
    if (!chars.length) return
    const next = [...digits]
    chars.forEach((c, i) => (next[from + i] = c))
    update(next)
    refs.current[Math.min(from + chars.length, length - 1)]?.focus()
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div key={shake} role="group" aria-label={label} className={cn('flex gap-3', shake > 0 && error && 'animate-ds-shake')}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el
            }}
            value={d}
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            autoFocus={autoFocus && i === 0}
            disabled={disabled}
            maxLength={length}
            aria-label={`Digit ${i + 1} of ${length}`}
            aria-invalid={error ? true : undefined}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '')
              if (v.length > 1) return fill(v, i)
              const next = [...digits]
              next[i] = v
              update(next)
              if (v) refs.current[i + 1]?.focus()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !digits[i] && i > 0) {
                const next = [...digits]
                next[i - 1] = ''
                update(next)
                refs.current[i - 1]?.focus()
              }
              if (e.key === 'ArrowLeft') refs.current[i - 1]?.focus()
              if (e.key === 'ArrowRight') refs.current[i + 1]?.focus()
            }}
            onPaste={(e) => {
              e.preventDefault()
              fill(e.clipboardData.getData('text'), i)
            }}
            onFocus={(e) => e.target.select()}
            className={cn(
              'h-14 w-[52px] rounded-lg border bg-card text-center font-display text-title2 text-foreground outline-none transition-[border-color,box-shadow] motion-fast focus:border-ring focus:ring-focus-soft disabled:opacity-60',
              error ? 'border-danger' : 'border-input',
            )}
          />
        ))}
      </div>
      {error && (
        <p role="alert" className="text-caption text-danger-soft-foreground">
          {error}
        </p>
      )}
    </div>
  )
}

export const formatCountdown = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

/** `restartToken` changing restarts the countdown. */
export function useCountdown(seconds: number, restartToken: unknown = 0, onDone?: () => void): { remaining: number; restart: () => void } {
  const [remaining, setRemaining] = useState(seconds)
  const [token, setToken] = useState(0)
  useEffect(() => {
    setRemaining(seconds)
    const id = window.setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          window.clearInterval(id)
          onDone?.()
          return 0
        }
        return r - 1
      })
    }, 1000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds, restartToken, token])
  return { remaining, restart: () => setToken((t) => t + 1) }
}

export function DsCountdown({ seconds, children, restartToken, onDone }: { seconds: number; restartToken?: unknown; onDone?: () => void; children: (remaining: number, restart: () => void) => ReactNode }) {
  const { remaining, restart } = useCountdown(seconds, restartToken, onDone)
  return <>{children(remaining, restart)}</>
}
